<template>
  <o-dialog
    ref="dialog"
    :title="$t('components.serviceCategory.actions.delete.title')"
    :info="info"
    :options="[
      { text: $t('general.cancel'), type: 'cancel' },
      { text: $t('general.confirm'), type: 'confirm', variant: 'danger', callback: confirm, disabled },
    ]"
    :portal="portal"
  >
    <div class="-mx-5 px-5 border-t border-b bg-gray-50 py-4">
      <p class="text-sm text-gray-800 mb-5 font-semibold" v-html="$t('components.serviceCategory.actions.delete.whatDo')" />
      <o-field-group :loading="fetching">
        <select-field
          v-model="value"
          :options="options"
          :loading="fetching"
        />
      </o-field-group>

      <o-field-group
        v-if="value === 'delete'"
        :label="$t('components.serviceCategory.actions.delete.confirm')"
        :info="$t('components.serviceCategory.actions.delete.confirmInfo')"
        col
        reverse
        class="mt-4 block"
      >
        <checkbox-field
          v-model="confirmDelete"
        />
      </o-field-group>
    </div>
  </o-dialog>
</template>

<script setup lang="ts">
import { OFieldGroup } from '@/components/elements';
import { SelectField, CheckboxField } from '@/components/fields';

import type { ServiceCategory } from '~/_api/models/serviceCategory';

interface Props {
  portal?: string;
  loading?: boolean;
}

interface Emits {
  (e: 'update:loading', value: boolean): void;
  (e: 'success'): void;
  (e: 'error'): void;
}

const props = withDefaults(defineProps<Props>(), {
  portal: undefined,
  loading: false,
});

const emit = defineEmits<Emits>();

const { $api, $notification, $i18n, $bus } = useNuxtApp();

const dialog = ref();
const serviceCategory = ref<ServiceCategory | null>(null);
const value = ref<ServiceCategory | 'delete' | null>(null);
const fetching = ref(false);
const serviceCategories = ref<ServiceCategory[] | null>(null);
const confirmDelete = ref(false);

const info = computed(() => {
  return $i18n.t('components.serviceCategory.actions.delete.info', {
    name: serviceCategory.value?.name,
    id: serviceCategory.value?.idServiceCategory,
  });
});

const options = computed(() => {
  const categories = serviceCategories.value;
  const opts: Array<{ label: string; value: ServiceCategory | 'delete' | null }> = [
    { label: $i18n.t('general.noCategory') as string, value: null },
    { label: $i18n.t('components.serviceCategory.actions.delete.services') as string, value: 'delete' },
  ];

  if (categories) {
    for (const category of categories) {
      opts.push({
        label: category.name,
        value: category,
      });
    }
  }

  return opts;
});

const disabled = computed(() => {
  if (value.value === 'delete' && confirmDelete.value === false) {
    return true;
  }
  return false;
});

const open = (category: ServiceCategory) => {
  serviceCategory.value = category;
  value.value = null;
  confirmDelete.value = false;

  dialog.value?.open();

  fetchServiceCategories();
};

const confirm = async () => {
  if (!serviceCategory.value) return;

  emit('update:loading', true);

  try {
    const data: Record<string, any> = {};

    if (value.value) {
      if (value.value === 'delete') {
        data.deleteAllServices = true;
      } else {
        data.moveToCategory = value.value.idServiceCategory;
      }
    }

    const response = await $api.serviceCategory.delete(serviceCategory.value, data);

    emit('update:loading', false);

    if (response.success) {
      $notification.toast({
        title: $i18n.t('components.serviceCategory.actions.delete.success'),
        text: $i18n.t('components.serviceCategory.actions.delete.successInfo', { name: serviceCategory.value.name }),
        variant: 'danger',
      });

      emit('success');

      $bus?.emit?.deleted('ServiceCategory', serviceCategory.value);

      return true;
    }
  } catch (e) {
    $notification.toast({
      title: $i18n.t('components.serviceCategory.actions.delete.error'),
      text: $i18n.t('components.serviceCategory.actions.delete.errorInfo', { name: serviceCategory.value?.name }),
    });

    emit('error');
    emit('update:loading', false);
  }
};

const fetchServiceCategories = async () => {
  fetching.value = true;

  try {
    const response = await $api.serviceCategory.list();
    const items = response.items;

    serviceCategories.value = items.filter(
      (cat: ServiceCategory) => cat.idServiceCategory !== serviceCategory.value?.idServiceCategory
    );
  } catch (e: any) {
    console.log(e);
    $notification.error({ errors: e.errors });
  }

  fetching.value = false;
};

defineExpose({
  open,
});
</script>
